import { type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode, useCallback } from 'react'
import tw, { TwStyle } from 'twin.macro'

interface SegmentedControlProps<T extends string | number> extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: T | undefined
  disabled?: boolean
  twStyle?: TwStyle
  segmentStyle?: TwStyle
  activeSegmentStyle?: TwStyle
  options:
    | {
        value: T
        content: ReactNode
        buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>
      }[]
    | undefined
  isActive?: (val: T | undefined) => boolean
  onChange: (option: T) => void
}

interface StyledSegmentProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isActive: boolean
  twStyle?: TwStyle
  activeSegmentStyle?: TwStyle
  children: ReactNode
}

type SegmentedControlContainerProps = HTMLAttributes<HTMLDivElement> & {
  twStyle?: TwStyle
}

const StyledSegment = ({ isActive, activeSegmentStyle, children, twStyle, ...rest }: StyledSegmentProps) => {
  return (
    <button
      css={[
        tw`py-2 inline-flex items-center justify-center flex-1 rounded-[calc(12px - 4px)] transition-colors text-[#636366] dark:text-[#D1D1D6]`,
        twStyle,
        isActive && [tw`bg-[#FFFFFF] text-[#0F0F0F] dark:(text-[#FFFFFF] bg-[#1A1A1A])`, activeSegmentStyle]
      ]}
      {...rest}
    >
      {children}
    </button>
  )
}

const SegmentedControlContainer = ({ children, twStyle }: SegmentedControlContainerProps) => {
  return <div css={[tw`flex p-1 rounded-xl bg-[#EAECED] dark:bg-[#2B2B2E]`, twStyle]}>{children}</div>
}

const SegmentedControl = <T extends string | number>({
  value,
  disabled,
  options = [],
  segmentStyle,
  activeSegmentStyle,
  twStyle,
  isActive: isActiveFn,
  onChange,
  ...rest
}: SegmentedControlProps<T>) => {
  const handleClick = useCallback(
    (value: T) => {
      onChange(value)
    },
    [onChange]
  )

  return (
    <SegmentedControlContainer twStyle={twStyle} {...rest}>
      {options.map(option => {
        const isActive = isActiveFn?.(option.value) || value === option.value
        return (
          <StyledSegment
            type='button'
            key={option.value}
            twStyle={segmentStyle}
            isActive={isActive}
            disabled={disabled}
            activeSegmentStyle={activeSegmentStyle}
            onClick={() => !isActive && handleClick(option.value)}
            {...option.buttonProps}
          >
            {option.content}
          </StyledSegment>
        )
      })}
    </SegmentedControlContainer>
  )
}

export default SegmentedControl
